import { FunctionComponent, useState } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Grid2 as Grid,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import { DateRangePicker } from '@starjack/shared-components';
import { useFormik } from 'formik';
import { DateRange } from '@starjack/utils';
import ArrowRightIcon from '@heroicons/react/24/solid/ArrowRightIcon';
import { AggregatedTicketSearchParams } from '../../aggregatedTicket';
import { Environment } from '../../environment';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

type OrderRangePreset = 'last30days' | 'last6months' | 'custom';

export type B2BUser = {
    id: string;
    name: string;
};

type StatisticFilterValues = {
    b2bUserId: string;
    orderRange: DateRange;
    productionRange: DateRange;
    ticketStartRange: DateRange;
    cancelRange: DateRange;

    productionEnvironment: string;
};

type StatisticsFilterProps = {
    onSearch: (
        params: Omit<AggregatedTicketSearchParams, 'environments'>
    ) => void;
    loading?: boolean;
    b2bUsers?: B2BUser[];
    environments?: Environment[];
};

export const StatisticsFilter: FunctionComponent<StatisticsFilterProps> = ({
    onSearch,
    loading,
    b2bUsers = [],
    environments = [],
}) => {
    const b2bSelectEnabled =
        b2bUsers.length > 0 &&
        !environments.includes(Environment.HOSPITALITY) &&
        !environments.includes(Environment.INTEGRATION);
    const [orderRangePreset, setOrderRangePreset] =
        useState<OrderRangePreset>('last30days');

    const formik = useFormik<StatisticFilterValues>({
        initialValues: {
            b2bUserId: '-',
            orderRange: getPresetRange('last30days'),
            productionRange: [undefined, undefined],
            ticketStartRange: [undefined, undefined],
            cancelRange: [undefined, undefined],
            productionEnvironment: '-',
        },
        onSubmit: (values) => {
            const [orderDateFrom, orderDateTo] = values.orderRange;
            const [productionDateFrom, productionDateTo] =
                values.productionRange;
            const [ticketStartFrom, ticketStartTo] = values.ticketStartRange;
            const [cancellationDateStartFrom, cancellationDateStartTo] =
                values.cancelRange;

            const { productionEnvironment, b2bUserId } = values;

            onSearch({
                userId: b2bUserId === '-' ? undefined : b2bUserId,
                orderDateFrom: orderDateFrom ?? undefined,
                orderDateTo: orderDateTo ?? undefined,
                productionDateFrom: productionDateFrom ?? undefined,
                productionDateTo: productionDateTo ?? undefined,
                ticketStartFrom: ticketStartFrom ?? undefined,
                ticketStartTo: ticketStartTo ?? undefined,
                cancellationDateStartFrom:
                    cancellationDateStartFrom ?? undefined,
                cancellationDateStartTo: cancellationDateStartTo ?? undefined,
                identificationType:
                    productionEnvironment === '-'
                        ? undefined
                        : productionEnvironment,
            });
        },

        onReset: () => {
            setOrderRangePreset('last30days');
            onSearch({});
        },
    });

    const handlePresetChange = (preset: OrderRangePreset) => {
        setOrderRangePreset(preset);
        if (preset !== 'custom') {
            formik.setFieldValue('orderRange', getPresetRange(preset));
        } else {
            formik.setFieldValue('orderRange', [undefined, undefined]);
        }
    };
    const { t } = useTranslation();

    const productionTypeOptions = [
        ['-', t('productionTypes.all')],
        ['BARCODE', t('productionTypes.eTicket')],
        ['PICKUP', t('productionTypes.pickupVoucher')],
        ['KEYCARD', t('productionTypes.keycard')],
        ['MOBILE_FLOW', t('productionTypes.mobileTicket')],
        ['INTERNAL_PRODUCTION', t('productionTypes.internalProduction')],
    ];

    const setPickerValue =
        (name: keyof StatisticFilterValues) => (value: unknown) => {
            formik.setFieldValue(name, value);
        };

    return (
        <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <Card>
                <CardContent
                    sx={{
                        px: { xs: 2, sm: 3 },
                        '&:last-child': { pb: { xs: 2, sm: 3 } },
                    }}
                >
                    <Grid spacing={{ xs: 3, md: 6 }} container>
                        <Grid size={{ md: 12 }}>
                            <OrderRangePresetPicker
                                title={t('order')}
                                preset={orderRangePreset}
                                onPresetChange={handlePresetChange}
                                value={formik.values['orderRange']}
                                onRangeChange={setPickerValue('orderRange')}
                                fromText={t('start')}
                                toText={t('to')}
                                presetLabels={{
                                    last30days: t('last30Days'),
                                    last6months: t('last6Months'),
                                    custom: t('customSelection'),
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography sx={{ mb: 2 }} variant='h6'>
                                {t('b2b')}
                            </Typography>
                            <TextField
                                fullWidth
                                label={t('b2bPartnerCompany')}
                                select
                                disabled={!b2bSelectEnabled}
                                value={formik.values['b2bUserId']}
                                onChange={(e) => {
                                    formik.setFieldValue(
                                        'b2bUserId',
                                        e.target.value
                                    );
                                }}
                                SelectProps={{ native: true }}
                            >
                                <option value='-'>{t('all')}</option>
                                {b2bUsers.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.name}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <DateRangePicker
                                onChange={setPickerValue('productionRange')}
                                value={formik.values['productionRange']}
                                title={t('production')}
                                fromText={t('start')}
                                toText={t('to')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <DateRangePicker
                                onChange={setPickerValue('ticketStartRange')}
                                value={formik.values['ticketStartRange']}
                                title={t('ticketStart')}
                                fromText={t('start')}
                                toText={t('to')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <DateRangePicker
                                onChange={setPickerValue('cancelRange')}
                                value={formik.values['cancelRange']}
                                title={t('cancellation')}
                                fromText={t('start')}
                                toText={t('to')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography sx={{ mb: 2 }} variant='h6'>
                                {t('productionType')}
                            </Typography>
                            <TextField
                                fullWidth
                                label={t('productionType')}
                                required
                                select
                                value={formik.values['productionEnvironment']}
                                onChange={(e) => {
                                    const { value } = e.target;

                                    formik.setFieldValue(
                                        'productionEnvironment',
                                        value
                                    );
                                }}
                                SelectProps={{ native: true }}
                            >
                                {productionTypeOptions.map(([value, label]) => (
                                    <option key={String(value)} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid display='flex' alignItems='center' size={12}>
                            <Stack direction='row' spacing={3} flexWrap='wrap'>
                                <LoadingButton
                                    loading={loading}
                                    type='submit'
                                    variant='contained'
                                >
                                    {t('search')}
                                    <SvgIcon
                                        sx={{
                                            ml: 2,
                                        }}
                                    >
                                        <ArrowRightIcon />
                                    </SvgIcon>
                                </LoadingButton>

                                {formik.dirty && (
                                    <Button
                                        sx={{
                                            color: 'text.secondary',
                                            textTransform: 'capitalize',
                                        }}
                                        type='reset'
                                        variant='text'
                                    >
                                        {t('reset')}
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </form>
    );
};

type OrderRangePresetPickerProps = {
    title: string;
    preset: OrderRangePreset;
    onPresetChange: (preset: OrderRangePreset) => void;
    value: DateRange;
    onRangeChange: (range: DateRange) => void;
    fromText: string;
    toText: string;
    presetLabels: {
        last30days: string;
        last6months: string;
        custom: string;
    };
};

const OrderRangePresetPicker: FunctionComponent<
    OrderRangePresetPickerProps
> = ({
    title,
    preset,
    onPresetChange,
    value,
    onRangeChange,
    fromText,
    toText,
    presetLabels,
}) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ mb: 2 }} variant='h6'>
            {title}
        </Typography>
        <Stack
            direction='row'
            spacing={4}
            alignItems='center'
            flexWrap='wrap'
            sx={{ minHeight: 56 }}
        >
            <ButtonGroup size='small'>
                <Button
                    variant={preset === 'last30days' ? 'contained' : 'outlined'}
                    onClick={() => onPresetChange('last30days')}
                >
                    {presetLabels.last30days}
                </Button>
                <Button
                    variant={
                        preset === 'last6months' ? 'contained' : 'outlined'
                    }
                    onClick={() => onPresetChange('last6months')}
                >
                    {presetLabels.last6months}
                </Button>
                <Button
                    variant={preset === 'custom' ? 'contained' : 'outlined'}
                    onClick={() => onPresetChange('custom')}
                >
                    {presetLabels.custom}
                </Button>
            </ButtonGroup>
            {preset === 'custom' && (
                <DateRangePicker
                    onChange={onRangeChange}
                    value={value}
                    fromText={fromText}
                    toText={toText}
                />
            )}
        </Stack>
    </Box>
);

export const getPresetRange = (
    preset: 'last30days' | 'last6months'
): DateRange => {
    const now = new Date();
    const from = new Date();
    if (preset === 'last30days') {
        from.setDate(now.getDate() - 30);
    } else {
        from.setMonth(now.getMonth() - 6);
    }
    return [from, now];
};
